import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { AuthenticationService } from '@shared/services/auth/authentication.service';

/**
 * Respuesta tipada del backend para upload/download/delete.
 * El backend devuelve siempre `{state, statusCode, message, data, count}`.
 */
export interface ApiResponse<T> {
  state:       'success' | 'error';
  statusCode:  number;
  message:     string;
  data:        T;
  count?:      number;
}

/**
 * Datos de un archivo (lo que devuelve upload o download).
 */
export interface UploadedFile {
  filename:     string;   // key completa en S3 (incluye la carpeta del ambiente: dev/ o prod/)
  bucket:       string;
  contentType?: string;
  size:         number;
  url:          string;   // presigned URL — usar directamente en <img src> o <a href>
  expiresIn:    number;   // segundos
}

/**
 * Parámetros para subir un archivo.
 *  - path: ruta lógica (ej. "receptions"). NO incluyas "dev/" ni "prod/": el API antepone la carpeta del ambiente.
 *          Ejemplos: "users/abc-123", "reports", "contracts"
 *  - folio: opcional. Sub-carpeta dentro de path (típicamente un id de orden, contrato, etc.)
 *  - file: el File del input.
 */
export interface UploadParams {
  path:   string;
  folio?: string;
  file:   File;
}

export interface DownloadParams {
  path:   string;
  folio?: string;
}

export interface DeleteParams {
  path:     string;
  folio?:   string;
  filename: string;   // solo el nombre del archivo, no el path completo
}

@Injectable({ providedIn: 'root' })
export class UploadDownloadService {

  private readonly authService = inject(AuthenticationService);

  /**
   * URL del endpoint. Se construye desde el environment.
   * El backend monta esto en POST /uploadPDF (nombre histórico, ahora maneja también imágenes).
   */
  private get endpoint(): string {
    const apiUrl = (environment as { API_URL?: string }).API_URL;
    if (!apiUrl) {
      throw new Error('API_URL no está configurado en el environment.');
    }
    return `${apiUrl}/uploadPDF`;
  }

  /**
   * Sube un archivo. Devuelve los datos del archivo subido (incluyendo presigned URL).
   */
  async upload(params: UploadParams): Promise<UploadedFile> {
    const form = new FormData();
    form.append('type',  'upload');
    form.append('path',  params.path);
    form.append('folio', params.folio ?? '');
    form.append('doc',   params.file);

    const response = await this.request<UploadedFile[]>(form);
    if (!Array.isArray(response.data) || response.data.length === 0) {
      throw new Error('La respuesta del servidor no contiene el archivo subido.');
    }
    return response.data[0];
  }

  /**
   * Lista los archivos en un path/folio dado.
   * Devuelve un array (puede estar vacío si no hay archivos).
   *
   * Usa `type: 'list'`, que devuelve METADATOS + presigned URL de cada
   * archivo. Antes mandaba `type: 'download'`, que baja el contenido de todos
   * los archivos del prefijo en base64 dentro del JSON: con un par de PDFs (o
   * un video) revienta el límite de 6 MB de respuesta de Lambda, y además el
   * shape que devuelve son strings `data:` — no `UploadedFile`.
   */
  async list(params: DownloadParams): Promise<UploadedFile[]> {
    const form = new FormData();
    form.append('type',  'list');
    form.append('path',  params.path);
    form.append('folio', params.folio ?? '');

    const response = await this.request<UploadedFile[]>(form);
    return Array.isArray(response.data) ? response.data : [];
  }

  /**
   * Genera una presigned URL fresca a partir del path/key S3 de un archivo
   * ya existente. Útil cuando lo que persistimos en BD es el path (no la URL,
   * que caduca en 1h).
   *
   * `filename` debe ser el path completo en S3 (lo que devolvió un upload
   * previo como `UploadedFile.filename`).
   */
  async presign(filename: string): Promise<UploadedFile | null> {
    if (!filename) return null;

    const form = new FormData();
    form.append('type',     'presign');
    form.append('path',     '');     // el endpoint requiere path; va vacío para presign
    form.append('filename', filename);

    const response = await this.request<UploadedFile | UploadedFile[]>(form);

    // ⚠️ El controlador del backend envuelve SIEMPRE la respuesta en un array
    // (`if not isinstance(usecaseResponse, list): usecaseResponse = [resp]`),
    // así que `data` llega como `[{ url, filename, ... }]`. Devolver
    // `response.data` tal cual hacía que `file.url` fuera `undefined` y por eso
    // la vista previa salía en blanco y el botón "Descargar" no hacía nada.
    // Aceptamos ambas formas para ser tolerantes a cambios del backend.
    const data: any = response.data;
    const file: UploadedFile | null = Array.isArray(data) ? (data[0] ?? null) : (data ?? null);

    return file && file.url ? file : null;
  }

  /**
   * Borra un archivo específico.
   */
  async delete(params: DeleteParams): Promise<void> {
    const form = new FormData();
    form.append('type',     'delete');
    form.append('path',     params.path);
    form.append('folio',    params.folio ?? '');
    form.append('filename', params.filename);

    await this.request<unknown>(form);
  }

  // ─── Helper privado ────────────────────────────────────────────────────────

  private async request<T>(body: FormData): Promise<ApiResponse<T>> {
    const idToken = await this.authService.getIdToken();
    if (!idToken) {
      throw new Error('No hay sesión activa.');
    }

    const response = await fetch(this.endpoint, {
      method:  'POST',
      headers: { Authorization: `Bearer ${idToken}` },
      body,
    });

    if (!response.ok) {
      const errText = await this.tryReadError(response);
      throw new Error(`Error ${response.status}: ${errText}`);
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      throw new Error('La respuesta del servidor no es JSON válido.');
    }

    const json = await response.json() as ApiResponse<T>;
    if (json.state === 'error') {
      throw new Error(json.message || 'Error desconocido.');
    }
    return json;
  }

  private async tryReadError(response: Response): Promise<string> {
    try {
      const json = await response.json();
      return json?.message ?? response.statusText;
    } catch {
      return response.statusText;
    }
  }
}